import { useId, type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCY_CHOICES, fmtMoney } from "../converters/money";
import { parseNumber } from "../converters/numberInput";

/** Typed text → number. Blank counts as `blank` (default 0); unreadable text is NaN so the caller can show an error. */
export function num(s: string, blank = 0): number {
  if (!s.trim()) return blank;
  const n = parseNumber(s);
  return n === null ? NaN : n;
}
export const isBad = (s: string) => s.trim() !== "" && parseNumber(s) === null;

export function currencySymbol(code: string): string {
  try {
    return new Intl.NumberFormat(code === "INR" ? "en-IN" : "en-US", { style: "currency", currency: code, currencyDisplay: "narrowSymbol" }).formatToParts(0).find((p) => p.type === "currency")?.value ?? code;
  } catch { return code; }
}

/** Money text with a real minus sign for negatives. */
export function moneyText(n: number, currency: string, decimals = 2): string {
  if (!Number.isFinite(n)) return "—";
  const t = fmtMoney(Math.abs(n), currency, decimals);
  return n < 0 && Math.abs(n) >= 0.5 * Math.pow(10, -decimals) ? "−" + t : t;
}

export function Money({ n, currency, decimals = 2, className = "" }: { n: number; currency: string; decimals?: number; className?: string }) {
  const neg = Number.isFinite(n) && n < 0 && Math.abs(n) >= 0.5 * Math.pow(10, -decimals);
  return <span className={`${className} ${neg ? "text-red-600" : ""}`}>{moneyText(n, currency, decimals)}</span>;
}

export function NumField({ label, value, onChange, hint, error }: { label: ReactNode; value: string; onChange: (v: string) => void; hint?: string; error?: string }) {
  const bad = isBad(value);
  const id = useId();
  return (
    <div>
      <Label htmlFor={id} className="mb-2 block">{label}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} inputMode="decimal" aria-invalid={bad || !!error} className={bad || error ? "border-red-500" : ""} />
      {bad ? <p className="text-xs text-red-600 mt-1">Enter a number, like 1,250.50</p> : error ? <p className="text-xs text-red-600 mt-1">{error}</p> : hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
    </div>
  );
}

export function CurrencySelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <Label className="text-xs text-muted-foreground">Currency</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 w-[190px]" aria-label="Currency"><SelectValue /></SelectTrigger>
        <SelectContent>{CURRENCY_CHOICES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
